import { useNavigate } from 'react-router-dom';

import type { CrudLayoutProps } from './model';
import type { FieldValues } from 'react-hook-form';
import '../utils/CrudLayout.css';

const FORM_FORMATS = {
  FULL: 'full',
  TWO_COLUMN: 'two-column',
  DEFAULT: 'default',
} as const;

const getFormClass = (formUiFormat?: string, customClass?: string) => {
  if (customClass) return customClass;

  switch (formUiFormat) {
    case FORM_FORMATS.FULL:
      return 'form-full-width';
    case FORM_FORMATS.TWO_COLUMN:
      return 'form-two-column';
    default:
      return 'form-default-width';
  }
};

const FormButtons = ({
  id,
  isLoading,
  isUpdating,
  backTo,
  formUiFormat,
  navigate,
}: {
  id?: string | number;
  isLoading: boolean;
  isUpdating: boolean;
  backTo: string;
  formUiFormat?: string;
  navigate: ReturnType<typeof useNavigate>;
}) => {
  const submitBtn = (
    <button
      type="submit"
      {...(formUiFormat === FORM_FORMATS.TWO_COLUMN
        ? { form: 'crud-form' }
        : {})}
      disabled={isLoading || isUpdating}
      className={`px-8 py-2.5 rounded-lg text-sm font-bold transition-all text-neutral-700 tracking-wide ${
        isLoading || isUpdating
          ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 cursor-not-allowed'
          : 'bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20 hover:shadow-brand-500/40'
      }`}
    >
      {id ? 'Edit' : 'Save'}
    </button>
  );

  const cancelBtn = (
    <button
      type="button"
      onClick={() => navigate(backTo)}
      disabled={isLoading || isUpdating}
      className="px-6 py-2.5 rounded-lg text-sm font-bold text-neutral-700 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-all"
    >
      Cancel
    </button>
  );

  return (
    <div className="flex justify-end gap-3 mt-8">
      {cancelBtn}
      {submitBtn}
    </div>
  );
};

const CrudLayout = <T extends FieldValues>({
  id,
  topTitle,
  children,
  backTo,
  isLoading,
  isUpdating,
  onSubmit,
  handleSubmit,
  showActivity,
  activityOnClickFn,
  formUiFormat,
  customeCssClass,
}: CrudLayoutProps<T>) => {
  const navigate = useNavigate();

  return (
    <div className="w-full bg-neutral-50 dark:bg-neutral-900">
      <div className="flex flex-col card dark:bg-neutral-800 gap-6 flex-1 min-h-0">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <h1 className="top-title">{topTitle}</h1>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigate(backTo)}
              className="btn-primary"
            >
              Back to List
            </button>
            {showActivity && id && (
              <button
                type="button"
                onClick={activityOnClickFn}
                className="btn-primary"
              >
                Activity
              </button>
            )}
          </div>
        </div>

        <div
          data-scroll-lock
          className="overflow-y-auto bg-white rounded-lg dark:bg-neutral-800 dark:border-neutral-700 p-6 shadow border border-neutral-200 h-dvh"
        >
          <form
            id="crud-form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            className={getFormClass(formUiFormat, customeCssClass)}
          >
            {children}

            {formUiFormat !== FORM_FORMATS.TWO_COLUMN && (
              <FormButtons
                id={id}
                isLoading={isLoading}
                isUpdating={isUpdating}
                backTo={backTo}
                formUiFormat={formUiFormat}
                navigate={navigate}
              />
            )}
          </form>

          {formUiFormat === FORM_FORMATS.TWO_COLUMN && (
            <FormButtons
              id={id}
              isLoading={isLoading}
              isUpdating={isUpdating}
              backTo={backTo}
              formUiFormat={formUiFormat}
              navigate={navigate}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CrudLayout;
